// Graph Builder用のカスタムフック

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useProjectStore } from '../../../stores/projectStore';
import { useDatasetSelection } from '../../../stores/hooks/useDatasetSelection';
import { graphRegistry } from '../GraphRegistry';
import {
  GraphBuilderState,
  GraphConfig,
  GraphCallbacks,
  HistogramConfig,
  ScatterPlotConfig,
  TimeSeriesConfig,
  BarChartConfig,
  PairPlotConfig,
  RugPlotConfig,
  AspectRatioPreset
} from '../../../../types/graph-builder.types';
import { CustomGraphConfig, createDefaultScatterLayer } from '../../../../types/custom-graph.types';
import { TabState } from '../../../../types/pane.types';
import type { GraphBuilderConfig } from '../../../../types/tabs';
import type { Report, GraphBuilderElement } from '../../../../types/report.types';
import { getFromDatasetRecord, createReportElementId, createReportId, createDatasetId, createColumnId } from '../../../../types';
import { findActiveDatasetIdForTab } from '../../../utils/activeDatasetUtils';
import { recommendColumnsForGraphType } from '../../../utils/graph-scale-utils';
import { appendElementReference } from '../../../utils/reportMarkdownParser';
import logger from '../../../utils/logger';

interface UseGraphBuilderProps {
  tab: TabState;
}

export function useGraphBuilder({ tab }: UseGraphBuilderProps) {
  const tabId = tab.id;
  const project = useProjectStore(state => state.project);
  const datasets = useMemo(() => project?.datasets || {}, [project?.datasets]);
  const updateProject = useProjectStore(state => state.updateProject);
  const updateTabState = useProjectStore(state => state.updateTabState);
  const findTab = useProjectStore(state => state.findTab);
  const getCurrentWorkspace = useProjectStore(state => state.getCurrentWorkspace);
  const workspaceLayout = getCurrentWorkspace()?.layoutState;

  // アクティブなデータセットIDを取得
  const activeDatasetId = useMemo(() => {
    if (!workspaceLayout) return null;
    return findActiveDatasetIdForTab(workspaceLayout, tab.id);
  }, [workspaceLayout, tab.id]);

  // タブの状態から初期selectedDatasetIdを先に読み込む（useDatasetSelectionで使用するため）
  const initialSelectedDatasetId = (tab?.tabViewState as GraphBuilderConfig | undefined)?.selectedDatasetId || activeDatasetId;

  // 選択状態を取得（初期値としてinitialSelectedDatasetIdを使用）
  const [currentDatasetIdForSelection, setCurrentDatasetIdForSelection] = useState(initialSelectedDatasetId);
  const { selectedRows, selectRows } = useDatasetSelection(currentDatasetIdForSelection || undefined);
  
  // タブの状態から初期値を読み込み
  const getInitialState = (): GraphBuilderState => {
    const savedConfig = tab?.tabViewState as GraphBuilderConfig | undefined;
    if (savedConfig) {
      return {
        selectedGraphType: savedConfig.selectedGraphType,
        selectedDatasetId: activeDatasetId || savedConfig.selectedDatasetId,
        currentConfig: savedConfig.currentConfig,
        validationError: savedConfig.validationError,
        previewKey: 0,
        aspectRatio: savedConfig.aspectRatio || '16:9'
      };
    }
    return {
      selectedGraphType: undefined,
      selectedDatasetId: activeDatasetId,
      currentConfig: undefined,
      validationError: undefined,
      previewKey: 0,
      aspectRatio: '16:9'
    };
  };

  // コンポーネントの状態
  const [state, setState] = useState<GraphBuilderState>(getInitialState);

  // フィルタ式の独立した状態（グラフタイプ選択前でも使用可能）
  const [filterExpression, setFilterExpression] = useState<string | undefined>(
    state.currentConfig?.filterExpression
  );

  // 注: activeDatasetIdの自動追跡は削除しました
  // タブ開いた時のデフォルト値（getInitialState）のみactiveDatasetIdを使用し、
  // その後はユーザーの手動選択を尊重します

  // 状態をタブに保存
  const saveStateToTab = useCallback((newState: GraphBuilderState) => {
    const tabInfo = findTab(tabId);
    if (!tabInfo) return;

    const newConfig: GraphBuilderConfig = {
      selectedGraphType: newState.selectedGraphType,
      selectedDatasetId: newState.selectedDatasetId,
      currentConfig: newState.currentConfig,
      validationError: newState.validationError,
      aspectRatio: newState.aspectRatio
    };

    updateTabState(tabInfo.container.id, tabId, (tabState) => ({
      ...tabState,
      tabViewState: newConfig
    }));
  }, [tabId, findTab, updateTabState]);

  // 状態が変更されたら保存
  useEffect(() => {
    saveStateToTab(state);
  }, [state, saveStateToTab]);


  // 選択されたデータセット
  const selectedDataset = useMemo(() => {
    if (!state.selectedDatasetId) return undefined;
    return getFromDatasetRecord(datasets, state.selectedDatasetId);
  }, [datasets, state.selectedDatasetId]);

  // 利用可能なグラフタイプのリスト（全て表示、フィルタリングなし）
  const graphTypes = useMemo(() => {
    return graphRegistry.getTypeList();
  }, []);

  // データセット変更時の処理
  const handleDatasetChange = (datasetId: string) => {
    logger.info('[GraphBuilder] Dataset changed', { datasetId });
    const datasetIdBranded = createDatasetId(datasetId);

    // データセット変更時はフィルタをリセット
    setFilterExpression(undefined);

    // 選択状態を追跡するためのデータセットIDも更新
    setCurrentDatasetIdForSelection(datasetIdBranded);

    setState(prev => ({
      ...prev,
      selectedDatasetId: datasetIdBranded,
      currentConfig: prev.currentConfig ? {
        ...prev.currentConfig,
        datasetId: datasetIdBranded,
        filterExpression: undefined
      } : undefined,
      previewKey: prev.previewKey + 1
    }));
  };

  // グラフタイプ変更時の処理
  const handleGraphTypeChange = (graphType: string) => {

    // デフォルト設定を作成
    let defaultConfig: GraphConfig | undefined;
    const datasetId = state.selectedDatasetId || activeDatasetId || createDatasetId('default');

    if (graphType === 'histogram') {
      defaultConfig = {
        type: 'histogram',
        datasetId: datasetId,
        column: createColumnId(''),
        bins: 10
      } as HistogramConfig;
    } else if (graphType === 'scatter') {
      defaultConfig = {
        type: 'scatter',
        datasetId: datasetId,
        xColumn: createColumnId(''),
        yColumn: createColumnId('')
      } as ScatterPlotConfig;
    } else if (graphType === 'timeseries') {
      // date/datetime型の列を探す
      let defaultXColumn = createColumnId('');
      if (datasetId) {
        const dataset = getFromDatasetRecord(datasets, datasetId);
        if (dataset) {
        const dateTimeColumns = dataset.schema.columns.filter(col =>
          col.type === 'date' || col.type === 'datetime'
        );

          if (dateTimeColumns.length === 1) {
            defaultXColumn = dateTimeColumns[0].id;
          }
        }
      }

      defaultConfig = {
        type: 'timeseries',
        datasetId: datasetId,
        xColumn: defaultXColumn,
        yColumns: []
      } as TimeSeriesConfig;
    } else if (graphType === 'bar') {
      defaultConfig = {
        type: 'bar',
        datasetId: datasetId,
        categoryColumn: createColumnId(''),
        valueColumns: [],
        aggregations: {},
        orientation: 'vertical',
        showValues: false,
        stackMode: 'grouped'
      } as BarChartConfig;
    } else if (graphType === 'pairplot') {
      defaultConfig = {
        type: 'pairplot',
        datasetId: datasetId,
        columns: []
      } as PairPlotConfig;
    } else if (graphType === 'rugplot') {
      // datetime型の列を探す
      let defaultColumn = createColumnId('');
      if (datasetId) {
        const dataset = getFromDatasetRecord(datasets, datasetId);
        if (dataset) {
          const datetimeColumns = dataset.schema.columns.filter(col => col.type === 'datetime');

          // datetime列が1つだけなら自動選択
          if (datetimeColumns.length === 1) {
            defaultColumn = datetimeColumns[0].id;
          }
        }
      }

      defaultConfig = {
        type: 'rugplot',
        datasetId: datasetId,
        column: defaultColumn,
        showIntensity: true
      } as RugPlotConfig;
    } else if (graphType === 'custom') {
      defaultConfig = {
        type: 'custom',
        datasetId: datasetId,
        layers: [createDefaultScatterLayer()],
        zoom: {
          enabled: true
        }
      } as CustomGraphConfig;
    }

    // フィルタ式を新しいconfigに反映
    if (defaultConfig && filterExpression) {
      defaultConfig.filterExpression = filterExpression;
    }

    setState(prev => ({
      ...prev,
      selectedGraphType: graphType,
      currentConfig: defaultConfig,
      validationError: undefined,
      previewKey: prev.previewKey + 1
    }));
  };

  // フィルタ変更時の処理
  const handleFilterChange = (expression: string | undefined) => {
    setFilterExpression(expression);

    // configが存在する場合は、configのfilterExpressionも更新
    setState(prev => ({
      ...prev,
      currentConfig: prev.currentConfig ? {
        ...prev.currentConfig,
        filterExpression: expression
      } : undefined,
      previewKey: prev.previewKey + 1
    }));
  };

  // アスペクト比変更時の処理
  const handleAspectRatioChange = (ratio: AspectRatioPreset) => {
    logger.info('[GraphBuilder] Aspect ratio changed', { ratio });
    setState(prev => ({
      ...prev,
      aspectRatio: ratio,
      previewKey: prev.previewKey + 1
    }));
  };

  // グラフ設定変更時の処理
  const handleConfigChange = (newConfig: GraphConfig) => {

    setState(prev => ({
      ...prev,
      currentConfig: newConfig,
      validationError: undefined,
      previewKey: prev.previewKey + 1
    }));
  };

  // 行選択のコールバック
  const graphCallbacks: GraphCallbacks = useMemo(() => ({
    onElementSelect: (rowIndices: number[], isAdditive: boolean) => {
      if (!currentDatasetIdForSelection) return;

      logger.info('[GraphBuilder] onElementSelect called', {
        rowIndices,
        isAdditive,
        currentDatasetIdForSelection,
        currentSelectedRows: selectedRows.length
      });

      let newSelection: number[];
      if (isAdditive) {
        const selectionSet = new Set(selectedRows);
        rowIndices.forEach(idx => {
          if (selectionSet.has(idx)) {
            selectionSet.delete(idx);
          } else {
            selectionSet.add(idx);
          }
        });
        newSelection = Array.from(selectionSet);
      } else {
        newSelection = rowIndices;
      }

      selectRows(newSelection);

    },
    selectedRows: selectedRows
  }), [currentDatasetIdForSelection, selectedRows, selectRows]);

  // 新規レポート作成
  const createNewReport = (name: string, description: string): Report => {
    logger.info('[GraphBuilder] createNewReport called', { name, description });
    
    const newReport: Report = {
      id: createReportId(`report-${crypto.randomUUID()}`),
      name: name || `Graph Report - ${new Date().toLocaleDateString()}`,
      description: description || 'Created from Graph Builder',
      content: '',
      elements: {},
      reportSettings: {
        contentWidth: 800,
        defaultElementMargin: 16,
        backgroundColor: '#ffffff',
        autoSave: false,
        autoSaveInterval: 5
      },
      metadata: {
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        createdBy: 'GraphBuilder',
        sharing: {
          isPublic: false,
          permissions: ['view', 'edit'] as const
        }
      }
    };

    updateProject((proj) => {
      return {
        ...proj,
        reports: [...(proj.reports || []), newReport]
      };
    });
    
    logger.info('[GraphBuilder] Created new report', { reportId: newReport.id });
    return newReport;
  };

  // グラフをレポートに追加
  const addGraphToReport = (reportId: string) => {
    if (!state.currentConfig || !state.selectedGraphType || !selectedDataset) return;

    const graphBuilderElement: GraphBuilderElement = {
      id: createReportElementId(`graph-${crypto.randomUUID()}`),
      type: 'graph_builder',
      graphConfig: state.currentConfig,
      datasetId: selectedDataset.id,
      title: state.currentConfig.title || `${state.selectedGraphType} - ${selectedDataset.name}`,
      height: 400,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    updateProject((proj) => {
      const updatedReports = (proj.reports || []).map(report => {
        if (report.id === reportId) {
          return {
            ...report,
            content: appendElementReference(report.content, graphBuilderElement.type, graphBuilderElement.id),
            elements: {
              ...report.elements,
              [graphBuilderElement.id]: graphBuilderElement
            },
            metadata: {
              ...report.metadata,
              lastModified: new Date().toISOString()
            }
          };
        }
        return report;
      });

      return {
        ...proj,
        reports: updatedReports
      };
    });

    logger.info('[GraphBuilder] Added graph to report', { reportId, elementId: graphBuilderElement.id });
  };

  // 選択されたグラフタイプに対する列の推奨
  const columnRecommendations = useMemo(() => {
    if (!state.selectedGraphType || !selectedDataset) {
      return null;
    }
    return recommendColumnsForGraphType(
      state.selectedGraphType,
      selectedDataset.schema.columns
    );
  }, [state.selectedGraphType, selectedDataset]);

  return {
    state,
    datasets,
    activeDatasetId,
    selectedDataset,
    selectedRows,
    graphTypes,
    graphCallbacks,
    filterExpression,
    handleDatasetChange,
    handleGraphTypeChange,
    handleConfigChange,
    handleFilterChange,
    handleAspectRatioChange,
    createNewReport,
    addGraphToReport,
    project,
    columnRecommendations
  };
}